import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DisplaySlotDto, DisplayTemplateDto, DisplayWithHealthDto, LayerDefinitionDto } from '../../../../api/gen';
import { DisplayState } from '../../../../api/gen/model/displayState';
import { LayoutStyleType } from '../../../../api/gen/model/layoutStyleType';
import { ContainerStyleConfig } from '../../../../api/gen/model/containerStyleConfig';
import { StateListComponent } from './state-list/state-list.component';
import { SlotListComponent, SaveLayoutPayload } from './slot-list/slot-list.component';
import { LayerListComponent } from './layer-list/layer-list.component';
import { defaultClosedLaneSlots } from '../../models/closed-lane-default-slots';

export type StateEditorMode = 'slot' | 'layer';

@Component({
  selector: 'app-edit-template-item',
  standalone: true,
  imports: [
    FormsModule, MatButtonModule, MatButtonToggleModule, MatDividerModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatSelectModule, StateListComponent, SlotListComponent, LayerListComponent
  ],
  templateUrl: './edit-template-item.component.html',
  styleUrl: './edit-template-item.component.scss'
})
export class EditTemplateItemComponent implements OnChanges {
  @Input() display!: DisplayWithHealthDto;
  @Input() template!: DisplayTemplateDto;
  @Input() saving = false;
  @Input() selectedState: DisplayState | null = null;
  @Input() errorMessage: string | null = null;
  @Output() stateSelected = new EventEmitter<DisplayState>();
  @Output() removeLayout = new EventEmitter<DisplayState>();
  @Output() saveName = new EventEmitter<string>();
  @Output() saveLayout = new EventEmitter<{ state: DisplayState; slots: Partial<DisplaySlotDto>[]; pendingImages: Map<string, File>; layers?: Partial<LayerDefinitionDto>[]; pendingLayerImages?: Map<string, File>; layoutStyle?: LayoutStyleType; transientDurationSeconds?: number; containerStyle?: ContainerStyleConfig }>();
  @Output() cloneState = new EventEmitter<{ source: DisplayState; target: DisplayState }>();

  templateName = '';
  /** Target state for the "copy this state to…" action. */
  cloneTargetState: DisplayState | null = null;
  readonly allStates = Object.values(DisplayState);
  readonly allLayoutStyles = Object.values(LayoutStyleType);

  /** Per-state mode override chosen by the user. Inferred from data if not set. */
  private stateModeOverrides = new Map<DisplayState, StateEditorMode>();

  /** Per-state layout style override chosen by the user. Falls back to saved value, then Stack. */
  private stateLayoutStyleOverrides = new Map<DisplayState, LayoutStyleType>();

  /** Per-state container style override chosen by the user. Falls back to the saved value. */
  private stateContainerStyleOverrides = new Map<DisplayState, ContainerStyleConfig>();

  ngOnChanges(): void {
    if (this.template) {
      this.templateName = this.template.name ?? '';
    }
  }

  /** Infer mode from existing state data, then fall back to override, then 'slot'. */
  getStateMode(state: DisplayState): StateEditorMode {
    const layout = this.template?.stateLayouts?.find(l => l.state === state);
    if (layout?.layers?.length) return 'layer';
    if (layout?.slots?.length) return 'slot';
    return this.stateModeOverrides.get(state) ?? 'slot';
  }

  setStateMode(state: DisplayState, mode: StateEditorMode): void {
    this.stateModeOverrides.set(state, mode);
  }

  get selectedEditorMode(): StateEditorMode {
    return this.selectedState ? this.getStateMode(this.selectedState) : 'slot';
  }

  get selectedStateSlots(): DisplaySlotDto[] | null {
    if (!this.selectedState || !this.template) return null;
    return this.template.stateLayouts?.find(l => l.state === this.selectedState)?.slots ?? null;
  }

  /**
   * What the slot editor starts from: the saved slots, or — for a reversible lane's closed states that have
   * no layout yet — a default "Einfahrt gesperrt" / "Ausfahrt gesperrt" layout the operator can save as is.
   * Cached per state so the editor gets the same array on every change-detection pass (a fresh array would
   * reset it and drop the operator's edits).
   */
  get slotsForEditor(): DisplaySlotDto[] | null {
    if (!this.selectedState) return null;
    const saved = this.selectedStateSlots;
    if (saved?.length || this.selectedStateLayers?.length) return saved;

    const defaults = defaultClosedLaneSlots(this.selectedState);
    if (!defaults) return saved;

    if (!this.defaultSlotsByState.has(this.selectedState)) {
      this.defaultSlotsByState.set(this.selectedState, defaults);
    }
    return this.defaultSlotsByState.get(this.selectedState)!;
  }

  private defaultSlotsByState = new Map<DisplayState, DisplaySlotDto[]>();

  get selectedStateLayers(): LayerDefinitionDto[] | null {
    if (!this.selectedState || !this.template) return null;
    return this.template.stateLayouts?.find(l => l.state === this.selectedState)?.layers ?? null;
  }

  get transientDurationSeconds(): number | undefined {
    if (!this.selectedState || !this.template) return undefined;

    const selectedStateLayout = this.template.stateLayouts?.find(l => l.state === this.selectedState);

    return selectedStateLayout?.transientDurationSeconds ?? undefined;
  }


  getStateLayoutStyle(state: DisplayState): LayoutStyleType {
    const override = this.stateLayoutStyleOverrides.get(state);
    if (override) return override;
    const saved = this.template?.stateLayouts?.find(l => l.state === state)?.layoutStyle;
    return saved ?? LayoutStyleType.Stack;
  }

  setStateLayoutStyle(state: DisplayState, style: LayoutStyleType): void {
    this.stateLayoutStyleOverrides.set(state, style);
  }

  get selectedStateLayoutStyle(): LayoutStyleType {
    return this.selectedState ? this.getStateLayoutStyle(this.selectedState) : LayoutStyleType.Stack;
  }

  getStateContainerStyle(state: DisplayState): ContainerStyleConfig {
    const override = this.stateContainerStyleOverrides.get(state);
    if (override) return override;
    const saved = this.template?.stateLayouts?.find(l => l.state === state)?.containerStyle;
    return saved ? { ...saved } : {};
  }

  setStateContainerStyleField(state: DisplayState, field: keyof ContainerStyleConfig, value: string): void {
    const current = this.getStateContainerStyle(state);
    this.stateContainerStyleOverrides.set(state, { ...current, [field]: value?.trim() ? value : null });
  }

  get selectedStateContainerStyle(): ContainerStyleConfig {
    return this.selectedState ? this.getStateContainerStyle(this.selectedState) : {};
  }

  onSaveLayout(payload: SaveLayoutPayload): void {
    if (!this.selectedState) return;
    this.saveLayout.emit({
      state: this.selectedState,
      ...payload,
      layoutStyle: this.getStateLayoutStyle(this.selectedState),
      containerStyle: this.getStateContainerStyle(this.selectedState),
      transientDurationSeconds: payload.transientDurationSeconds
    });
  }

  /** True when the selected state has a saved layout worth copying onto another state. */
  get selectedStateHasContent(): boolean {
    return !!(this.selectedStateSlots?.length || this.selectedStateLayers?.length);
  }

  /** States the current state can be copied onto (every state except itself). */
  get cloneTargetOptions(): DisplayState[] {
    return this.allStates.filter(s => s !== this.selectedState);
  }

  onCloneState(): void {
    if (!this.selectedState || !this.cloneTargetState || this.cloneTargetState === this.selectedState) return;
    this.cloneState.emit({ source: this.selectedState, target: this.cloneTargetState });
    this.cloneTargetState = null;
  }
}
